import { motion } from "framer-motion";
import { Gauge, TrendingUp, HeartPulse, Network } from "lucide-react";

const OUTCOMES = [
  {
    title: "Faster diagnoses",
    desc: "Cut reporting turnaround from hours to minutes without compromising quality.",
    icon: Gauge,
  },
  {
    title: "Better operational efficiency",
    desc: "Remove repetitive work so clinical teams focus on patients, not paperwork.",
    icon: TrendingUp,
  },
  {
    title: "Improved patient outcomes",
    desc: "Earlier insights mean earlier interventions — and measurably better care.",
    icon: HeartPulse,
  },
  {
    title: "More scalable delivery",
    desc: "Grow capacity without proportionally growing complexity, cost, or headcount.",
    icon: Network,
  },
];

export default function WhyZeltaa() {
  return (
    <section
      id="why"
      data-testid="why-section"
      className="relative bg-[#F9FBFD] pb-28 sm:pb-36"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Dark accent block */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-120px" }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="relative overflow-hidden rounded-3xl bg-[#071B34] p-10 sm:p-16 lg:p-20"
        >
          {/* Background effects */}
          <div className="intelligence-grid opacity-60" aria-hidden="true" />
          <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-[#14D6C7]/15 blur-[120px]" />
          <div className="absolute -bottom-24 -left-24 w-80 h-80 rounded-full bg-[#2EE8FF]/10 blur-[100px]" />
          <div className="noise-overlay" aria-hidden="true" />

          <div className="relative grid lg:grid-cols-12 gap-12 lg:gap-16">
            {/* Left copy */}
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-[#14D6C7] font-manrope font-bold mb-6">
                <span className="block w-8 h-px bg-[#14D6C7]" />
                Why Zeltaa
              </div>
              <h2 className="text-balance text-3xl sm:text-4xl lg:text-5xl font-manrope font-bold tracking-[-0.02em] text-white leading-[1.1]">
                Healthcare systems don&apos;t need more{" "}
                <span className="text-white/40 line-through decoration-[#14D6C7]/60 decoration-2">software</span>
                .
                <br />
                They need{" "}
                <span className="bg-gradient-to-r from-[#14D6C7] to-[#2EE8FF] bg-clip-text text-transparent">
                  intelligence
                </span>{" "}
                woven into the workflows that power care delivery.
              </h2>
              <p className="mt-7 max-w-xl text-base sm:text-lg font-poppins text-white/60 leading-relaxed">
                Zeltaa builds infrastructure that helps healthcare professionals focus on
                clinical decisions while intelligent systems handle the repetitive work
                around them.
              </p>
            </div>

            {/* Right outcomes */}
            <div className="lg:col-span-5">
              <div className="text-xs uppercase tracking-[0.22em] text-white/40 font-manrope font-semibold mb-5">
                The result
              </div>
              <ul className="space-y-1.5">
                {OUTCOMES.map((o, i) => {
                  const Icon = o.icon;
                  return (
                    <motion.li
                      key={o.title}
                      initial={{ opacity: 0, x: 20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.6, delay: 0.15 + i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                      className="group flex items-start gap-4 p-4 rounded-xl border border-white/[0.06] hover:border-[#14D6C7]/30 hover:bg-white/[0.025] transition-all duration-400"
                    >
                      <span className="flex-shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-lg bg-[#14D6C7]/10 border border-[#14D6C7]/20 text-[#14D6C7] group-hover:bg-[#14D6C7]/20 transition-colors">
                        <Icon className="w-4.5 h-4.5" strokeWidth={1.8} />
                      </span>
                      <div>
                        <div className="text-white font-manrope font-semibold text-[15px]">
                          {o.title}
                        </div>
                        <div className="mt-0.5 text-sm font-poppins text-white/50 leading-relaxed">
                          {o.desc}
                        </div>
                      </div>
                    </motion.li>
                  );
                })}
              </ul>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
